/**
 * LatestResultCard.jsx — Dashboard surface for the most recent completed simulation.
 *
 * Answers "what's my latest result and is it worth acting on?" in one card:
 *   Scenario name · short result line · key metrics (responses, coverage, context, reliability)
 *   · CTA → View Analysis
 */
import { Link } from 'react-router-dom';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { ArrowRight, Sparkles } from 'lucide-react';
import { cn } from '../../lib/utils';
import { ContextInfluenceBadge } from './ContextInfluenceBadge';
import ReliabilitySummary from './ReliabilitySummary';
import { formatDate } from '../../lib/formatters';

function formatResultLine(simulation) {
  // Try to derive a concise mode-neutral result line from simulation fields.
  const totalResponses =
    simulation.actual_n ?? simulation.total_responses ?? simulation.sample_size;
  if (simulation.collapse_flag || simulation.incomplete_census_flag) {
    return 'Completed with notable warnings — check Analysis before sharing.';
  }
  if (totalResponses) {
    return `Completed simulation with ${totalResponses.toLocaleString()} simulated responses, ready for analysis.`;
  }
  return 'Completed simulation ready for analysis.';
}

export default function LatestResultCard({ simulation, populationName = null, className, testId = 'latest-result-card' }) {
  if (!simulation) return null;

  const config = simulation.config || {};
  const contextMode = config.context_mode || simulation.context_mode || 'standard';
  const completedAt = simulation.completed_at || simulation.created_at;

  const totalN = simulation.population_total_n;
  const actualN = simulation.actual_n ?? simulation.total_responses ?? simulation.sample_size;
  const isFull = !!simulation.is_full_census;
  const incomplete = !!simulation.incomplete_census_flag;
  const coverageRate = simulation.coverage_rate;
  let coverageLabel = null;
  if (actualN != null && totalN != null) {
    const cov = coverageRate != null ? (coverageRate * 100).toFixed(0) : ((actualN / Math.max(totalN, 1)) * 100).toFixed(0);
    coverageLabel = `${actualN}/${totalN} (${cov}%)`;
  } else if (actualN != null) {
    coverageLabel = `${actualN.toLocaleString()} responses`;
  }
  const coverageTone = incomplete
    ? 'bg-warning-soft text-warning-soft-foreground border-warning-border'
    : isFull
    ? 'bg-positive-soft text-positive-soft-foreground border-positive-border'
    : 'bg-slate-50 text-slate-700 border-slate-200';

  return (
    <div
      className={cn(
        'bg-white dark:bg-card border border-slate-200 dark:border-border rounded-xl p-5 shadow-sm',
        className
      )}
      data-testid={testId}
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex-1 min-w-0 space-y-2">
          <div className="flex items-center gap-2 flex-wrap">
            <Sparkles className="w-4 h-4 text-warning" />
            <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
              Latest Result
            </p>
            {completedAt && (
              <span className="text-[10px] text-slate-400">· {formatDate(completedAt)}</span>
            )}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <h2
              className="text-base font-bold text-slate-900 dark:text-white tracking-tight truncate"
              data-testid={`${testId}-name`}
            >
              {simulation.name}
            </h2>
          </div>
          {populationName && (
            <p className="text-xs text-slate-500 dark:text-slate-400">
              Population: <span className="text-slate-700 dark:text-slate-200 font-medium">{populationName}</span>
            </p>
          )}
          <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed" data-testid={`${testId}-result-line`}>
            {formatResultLine(simulation)}
          </p>

          {/* Status badges */}
          <div className="flex items-center gap-2 flex-wrap pt-1">
            {coverageLabel && (
              <Badge
                variant="outline"
                className={cn('border px-2 py-0.5 text-[11px] font-medium', coverageTone)}
                data-testid={`${testId}-coverage`}
              >
                Coverage: {coverageLabel}
                {isFull && !incomplete && ' · full'}
                {incomplete && ' · incomplete'}
              </Badge>
            )}
            <ContextInfluenceBadge
              mode={contextMode}
              maxChars={config.context_max_chars}
              testId={`${testId}-context`}
            />
            <ReliabilitySummary
              mode="compact"
              simulation={simulation}
              testId={`${testId}-reliability`}
            />
          </div>
        </div>

        <Link to={`/analysis?simulation=${simulation.id}`} data-testid={`${testId}-cta`}>
          <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white">
            View Analysis
            <ArrowRight className="w-4 h-4 ml-1.5" />
          </Button>
        </Link>
      </div>
    </div>
  );
}
